// Рамка прозрачной подложки — градиентный штрих, как в SVG из Figma («Group 31»)
// и в мобилке (`design-block/transparent-frame.tsx`): #CC7E54 только у верхнего
// левого и нижнего правого углов, к середине рамка почти гаснет. CSS-бордер
// бывает только сплошным, поэтому рамка — SVG-слой поверх подложки.
//
// Штрих 1.4 стоит по краю, наружную половину срезает клип той же скруглённой
// формы — видна внутренняя 0.7, как в макете. Клип именно скруглённый: край
// SVG на углах ничего не срезает, и углы выходили бы вдвое жирнее.
import { useId } from 'react'
import { blockTheme } from '@teach/ui'

const db = blockTheme.designBlock
const STROKE = 0.7
// Посередине рамка в макете уходит почти в ноль — остаётся едва различимый
// след по всему краю (4 %), чтобы контур не рвался.
const BASE_COLOR = 'rgba(204, 126, 84, 0.04)'

// Угасание от верхнего левого угла: верх гаснет к середине ширины, левый край —
// чуть ниже нижнего края, так что нижний левый угол почти пустой (у второго
// угла — зеркально). Линия нуля задана в ДОЛЯХ сторон, поэтому градиент —
// линейная функция f = u / FADE_TOP + v / FADE_SIDE в долях подложки (u, v).
// Вектор SVG-градиента в objectBoundingBox ниже даёт ровно её: после растяжения
// на любую пропорцию подложки линии уровня остаются параллельны линии нуля в
// ПИКСЕЛЯХ (аффинное преобразование сохраняет прямые), и на вытянутой подложке
// угасание не встаёт вертикально.
const FADE_TOP = 0.5
const FADE_SIDE = 1.1
const A = 1 / FADE_TOP
const B = 1 / FADE_SIDE
const DX = A / (A * A + B * B)
const DY = B / (A * A + B * B)

const Stops = () => (
  <>
    <stop offset="0" stopColor={db.backdropTransparentBorder} />
    <stop offset="0.5" stopColor={db.backdropTransparentBorder} stopOpacity={0.3} />
    <stop offset="1" stopColor={db.backdropTransparentBorder} stopOpacity={0} />
  </>
)

export const TransparentFrame = ({ rounded }: { rounded: boolean }) => {
  const id = useId().replace(/:/g, '')
  const radius = rounded ? parseFloat(db.backdropTransparentRadius) : 0
  const strokes = [BASE_COLOR, `url(#${id}-tl)`, `url(#${id}-br)`]
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full overflow-hidden"
      data-transparent-frame
    >
      <defs>
        <linearGradient id={`${id}-tl`} x1={0} y1={0} x2={DX} y2={DY}>
          <Stops />
        </linearGradient>
        <linearGradient id={`${id}-br`} x1={1} y1={1} x2={1 - DX} y2={1 - DY}>
          <Stops />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <rect width="100%" height="100%" rx={radius} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        {strokes.map((stroke) => (
          <rect
            key={stroke}
            width="100%"
            height="100%"
            rx={radius}
            fill="none"
            stroke={stroke}
            strokeWidth={STROKE * 2}
          />
        ))}
      </g>
    </svg>
  )
}
